<template>
    <div v-if="!loading && subscriptions" class="w-full flex flex-column gap-3">
        <div class="flex gap-2 flex-wrap">
            <AdminAddSubscription @added="loadSubscriptions" :users="users" :projects="projects" :banks="banks" :statuses="statusOptions" />
            <Button label="Экспорт" icon="pi pi-download" @click="exportCSV" severity="secondary" size="small" class="flex"/>
            <Button label="Обновить" icon="pi pi-refresh" @click="loadSubscriptions" severity="secondary" size="small" class="flex"/>
            <Button label="Сбросить" icon="pi pi-times" @click="resetFilters" severity="secondary" size="small" class="flex"/>
        </div>
        <DataTable
            ref="dt"
            :value="subscriptions"
            responsiveLayout="scroll" 
            v-model:filters="lazyParams.filters"
            v-model:selection="selectedRows"
            selectionMode="multiple"
            @page="onPage($event)"
            @sort="onSort($event)"
            @filter="onFilter($event)"
            :globalFilterFields="globalFields"
            lazy
            filterDisplay="menu"
            :rows="lazyParams.rows"
            :totalRecords="totalSubscriptions"
            :loading="loading"
        >
            <template #header>
                <div class="text-sm flex flex-row gap-2 text-600 font-medium flex-wrap">
                    <span class="flex my-auto py-2">Итого: {{ totalSubscriptions }}</span>
                    <span v-if="selectedRows && selectedRows.length > 0" class="flex my-auto py-2">
                        Выбрано: {{ selectedRows.length }}{{ selectedAmountsText ? `, на сумму ${selectedAmountsText}` : '' }}
                    </span>
                    <Button
                        v-if="selectedRows && selectedRows.length > 0 && selectedAmountsText"
                        @click="copy(selectedAmountsText)"
                        icon="pi pi-copy"
                        iconPos="right"
                        text
                        severity="secondary"
                        class="flex text-xs"
                        size="small"
                    />
                </div>
            </template>

            <Column selectionMode="multiple" headerStyle="width: 3rem"></Column>

            <Column field="_id" header="" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>
                        <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="status" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">Статус</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select v-model="filterModel.value" @change="filterCallback()" :options="statusOptions" placeholder="Все" class="p-column-filter" :showClear="true">
                        <template #option="slotProps">
                            <Tag :value="getStatusTitle(slotProps.option)" class="text-xs" :severity="getStatusSeverity(slotProps.option)" />
                        </template>
                    </Select>
                </template>
                <template #body="{ data }">
                    <Tag :value="getStatusTitle(data.status)" class="text-xs" :severity="getStatusSeverity(data.status)" 
                        :class="(data.status=='completed') ? 'bg-green-500 text-green-100' : ''"/>
                </template>
            </Column>

            <Column field="">
                <template #header>
                    <span class="text-xs font-medium">Действия</span>
                </template>
                <template #body="{ data }">
                    <div class="flex flex-row gap-1"> 
                        <Button label="Проверить" @click="checkSubscriptionStatus(data.id)" :loading="loading_ && loadingSubscriptionId === data.id" class="text-xs" size="small" severity="info" outlined icon="pi pi-check"/>
                        <Button label="" @click="openChargeDialog(data.id)" class="text-xs" size="small" severity="warn" outlined icon="pi pi-wallet" :disabled="['pending','canceled', 'expired', 'failed'].includes(data.status)"/>
                        <Button label="" @click="openCancelDialog(data.id)" class="text-xs" size="small" severity="danger" outlined icon="pi pi-times" :disabled="['canceled', 'expired', 'failed'].includes(data.status)"/>
                        <Button @click="goToOrders(data.id)" :loading="loading_ && loadingSubscriptionId === data.id" class="text-xs" size="small" severity="secondary" outlined icon="pi pi-file"/>
                        <Button @click="goToEvents(data.id)" :loading="loading_ && loadingSubscriptionId === data.id" class="text-xs" size="small" severity="secondary" outlined icon="pi pi-calendar"/>
                        <Button @click="goToAttempts(data.id)" :loading="loading_ && loadingSubscriptionId === data.id" class="text-xs" size="small" severity="secondary" outlined icon="pi pi-clock"/>
                    </div>
                </template>
            </Column>

            <Column field="amount">
                <template #header>
                    <span class="text-xs font-medium">Сумма</span>
                </template>
                <template #body="{ data }">
                    <span v-if="data.amount !== null && data.amount !== undefined" class="white-space-nowrap flex flex-row gap-1 align-items-center">
                        <span class="white-space-nowrap">
                            {{ getFormatAmount(data.amount, data.currency || 'rub') }} {{ (data.currency || '').toUpperCase() }}
                        </span>
                        <Button @click="copy(`${data.amount}`)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </span>
                </template>
            </Column>

            <Column field="period_unit" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Период</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-10rem" placeholder="day, month..." />
                </template>
                <template #body="{ data }">
                    <div class="flex flex-row gap-1 align-items-center">
                        <Tag v-if="data.period_count" :value="data.period_count" severity="secondary" class="flex text-xs" size="small"/>
                        <Tag v-if="data.period_unit" :value="data.period_unit" severity="secondary" class="flex text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="success_count">
                <template #header>
                    <span class="text-xs font-medium">Успехи</span>
                </template>
                <template #body="{ data }">
                    <Tag v-if="data.success_count > 0" :value="data.success_count" severity="success" class="ml-1" size="small"/>
                </template>
            </Column>

            <Column field="bank_code" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">Банк</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select v-model="filterModel.value" @change="filterCallback()" :options="banks" optionValue="value" optionLabel="label" placeholder="Все" class="p-column-filter" :showClear="true"/>
                </template>
                <template #body="{ data }">
                    <span class="text-xs">{{ getBankLabel(data.bank_code) }}</span>
                </template>
            </Column>

            <Column field="user_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Обменник</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select v-model="filterModel.value" @change="filterCallback()" :options="users" filter optionValue="id" optionLabel="name" placeholder="Все" class="p-column-filter" :showClear="true"/>
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 justify-content-start align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 8rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ getUserTitle(data.user_id) }}
                            </span>
                        </span>
                        <Button @click="copy(data.user_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="client_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Клиент</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.client_id }}
                            </span>
                        </span>
                        <Button @click="copy(data.client_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column v-if="isUserInGroup('super_admin', accessCodes)" field="project_id" :showFilterMenu="true" :showFilterMatchModes="false" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">Проект</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Select v-model="filterModel.value" @change="filterCallback()" :options="projects" optionValue="value" optionLabel="label" placeholder="Все" class="p-column-filter" :showClear="true"/>
                </template>
                <template #body="{ data }">
                    <span class="text-xs">{{ getProjectLabel(data.project_id) }}</span>
                </template>
            </Column>

            <Column field="message">
                <template #header>
                    <span class="text-xs font-medium">Сообщение</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.message" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden bg-yellow-100" style="max-width: 12rem" v-tooltip="data.message">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.message }}
                            </span>
                        </span>
                        <Button @click="copy(data.message)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="subscription_token" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Токен</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.subscription_token" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 10rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ getShort(data.subscription_token, 10) }}
                            </span>
                        </span>
                        <Button @click="copy(data.subscription_token)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="ext_subscription_id" filterMatchMode="contains" :showFilterMenu="true" :showFilterMatchModes="false">
                <template #header>
                    <span class="text-xs font-medium">Внешний ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.ext_subscription_id" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 5rem">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.ext_subscription_id }}
                            </span>
                        </span>
                        <Button @click="copy(data.ext_subscription_id)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="approval_url">
                <template #header>
                    <span class="text-xs font-medium">Approval URL</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.approval_url" class="text-sm flex flex-row gap-1 align-items-center">
                        <a :href="data.approval_url" target="_blank" rel="noopener noreferrer" class="text-xs white-space-nowrap overflow-hidden text-blue-600" style="max-width: 10rem; text-overflow: ellipsis;">
                            {{ getShort(data.approval_url, 24) }}
                        </a>
                        <Button @click="copy(data.approval_url)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="first_charge_at">
                <template #header>
                    <span class="text-xs font-medium">Первое списание</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.first_charge_at) }}</span>
                </template>
            </Column>

            <Column field="next_charge_at">
                <template #header>
                    <span class="text-xs font-medium">Следующее списание</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.next_charge_at) }}</span>
                </template>
            </Column>

            <Column field="last_charge_at">
                <template #header>
                    <span class="text-xs font-medium">Последнее списание</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.last_charge_at) }}</span>
                </template>
            </Column>

            <Column field="retry_count">
                <template #header>
                    <span class="text-xs font-medium">Повторы</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs">{{ data.retry_count ?? 0 }}</span>
                </template>
            </Column>

            <Column field="processing_started_at">
                <template #header>
                    <span class="text-xs font-medium">Обработка начата</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.processing_started_at) }}</span>
                </template>
            </Column>

            <Column field="canceled_at">
                <template #header>
                    <span class="text-xs font-medium">Отменена</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.canceled_at) }}</span>
                </template>
            </Column>

            <Column field="created_at" filterField="created_at" sortable dataType="date" :showFilterMatchModes="false" :showFilterOperator="false">
                <template #header>
                    <span class="text-xs font-medium">Создана</span>
                </template>
                <template #filter="{ filterModel }">
                    <DatePicker v-model="filterModel.value" dateFormat="dd.mm.yy" placeholder="dd.mm.yy" mask="99.99.9999" showTime hourFormat="24" fluid/>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.created_at) }}</span>
                </template>
            </Column>

            <Column field="updated_at">
                <template #header>
                    <span class="text-xs font-medium">Обновлена</span>
                </template>
                <template #body="{ data }">
                    <span class="text-xs white-space-nowrap">{{ formatNullableDate(data.updated_at) }}</span>
                </template>
            </Column>

            <Column field="description">
                <template #header>
                    <span class="text-xs font-medium">Описание</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.description" class="text-sm flex flex-row gap-1 align-items-center">
                        <span class="text-xs flex my-auto overflow-hidden" style="max-width: 12rem" v-tooltip="data.description">
                            <span class="white-space-nowrap" style="text-overflow: ellipsis;">
                                {{ data.description }}
                            </span>
                        </span>
                        <Button @click="copy(data.description)" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <Column field="metadata">
                <template #header>
                    <span class="text-xs font-medium">Metadata</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.metadata" class="text-sm flex flex-row gap-1 align-items-center">
                        <Button @click="openMetadataModal(data.metadata)" icon="pi pi-eye" iconPos="right" text severity="info" class="text-xs" size="small"/>
                        <Button @click="copy(formatMetadata(data.metadata))" icon="pi pi-copy" iconPos="right" text severity="secondary" class="text-xs" size="small"/>
                    </div>
                </template>
            </Column>

            <template #footer>
                <div class="text-sm flex flex-row gap-2 text-600 font-medium flex-wrap">
                    <span class="flex my-auto py-2">Итого: {{ totalSubscriptions }}</span>
                    <span v-if="selectedRows && selectedRows.length > 0" class="flex my-auto py-2">
                        Выбрано: {{ selectedRows.length }}{{ selectedAmountsText ? `, на сумму ${selectedAmountsText}` : '' }}
                    </span>
                    <Button
                        v-if="selectedRows && selectedRows.length > 0 && selectedAmountsText"
                        @click="copy(selectedAmountsText)"
                        icon="pi pi-copy"
                        iconPos="right"
                        text
                        severity="secondary"
                        class="flex text-xs"
                        size="small"
                    />
                </div>
            </template>
        </DataTable>
        <Paginator
            v-model:first="lazyParams.first"
            :rows="lazyParams.rows"
            :totalRecords="totalSubscriptions"
            template="FirstPageLink  PageLinks LastPageLink RowsPerPageDropdown"
            :rowsPerPageOptions="[10, 20, 30, 50, 100, 200, 300, 400, 500, 1000, 2000, 3000, 4000, 5000]"
            @page="onPage"
        />

        <Dialog v-model:visible="metadataDialog.visible" modal header="Metadata" :style="{ width: 'min(900px, 95vw)' }">
            <div class="w-full overflow-auto">
                <pre class="text-sm m-0 white-space-pre-wrap line-height-3">{{ metadataDialog.content }}</pre>
            </div>
        </Dialog>

        <AdminSubscriptionCharge
            v-model="chargeDialog.visible"
            :subscription-id="chargeDialog.id"
            @charged="onSubscriptionCharged"
        />

        <AdminSubscriptionCancel
            v-model="cancelDialog.visible"
            :subscription-id="cancelDialog.id"
            @canceled="onSubscriptionCanceled"
        />
    </div>
</template>

<script>
import AdminAddSubscription from '@/components/admin/subscription/add.vue'
import AdminSubscriptionCancel from '@/components/admin/subscription/cancel.vue'
import AdminSubscriptionCharge from '@/components/admin/subscription/charge.vue'
import Helper from '@/assets/js/helper.js'
import BigNumber from 'bignumber.js'
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import Connect from '@/assets/js/connect.js'

export default {
    components: {
        AdminAddSubscription,
        AdminSubscriptionCancel,
        AdminSubscriptionCharge
    },
    props: {
        subscriptions: {
            type: Array,
            required: true
        },
        users: {
            type: Array,
            default: () => []
        },
        totalSubscriptions: {
            type: Number,
            default: 0
        },
        loading: {
            type: Boolean,
            default: false
        },
        defaultParams: {
            type: Object,
            default: () => ({})
        },
        filters: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['filter', 'lazyParams'],
    data() {
        return {
            loading_: false,
            loadingSubscriptionId: null,
            cancelDialog: {
                visible: false,
                id: null
            },
            chargeDialog: {
                visible: false,
                id: null
            },
            metadataDialog: {
                visible: false,
                content: ''
            },
            lazyParams: this.defaultParams,
            selectedRows: [],
            sort: {
                field: 'created_at',
                order: -1
            },
            globalFields: [
                'id',
                'ext_subscription_id',
                'subscription_token',
                'user_id',
                'client_id',
                'project_id',
                'status',
                'bank_code',
                'message',
                'description',
                'created_at'
            ],
            statusSeeds: ['pending', 'active', 'processing', 'canceled', 'failed', 'expired', 'paused'],
            banks: [
                {
                    value: 'dolinsk_bank',
                    label: 'Dolinsk Bank'
                },
                {
                    value: 'kanyonpay',
                    label: 'KanyonPay'
                },
                {
                    value: 'easy_pay',
                    label: 'Easy Pay'
                },
                {
                    value: 'loveandpay',
                    label: 'LoveAndPay'
                },
                {
                    value: 'netpay',
                    label: 'NetPay'
                },
                {
                    value: 'panacea_pay',
                    label: 'Panacea Pay'
                },
                {
                    value: 'tbank',
                    label: 'TBank'
                }
            ],
            projects: [
                {
                    value: '699ea1f3c9b225f0e92393c6',
                    label: 'X-HUB'
                },
                {
                    value: '699ead66c13e000266f72745',
                    label: 'PaySido'
                },
                {
                    value: '69ddfa476b47e9aea51ae0e2',
                    label: 'PaySame'
                }
            ],
            dt: null
        }
    },
    computed: {
        accessCodes() {
            return this.$store.getters.StateUserAccessCodes
        },
        statusOptions() {
            const dynamicStatuses = (this.subscriptions || [])
                .map(subscription => subscription.status)
                .filter(Boolean)
            return [...new Set([...this.statusSeeds, ...dynamicStatuses])]
        },
        selectedAmountsText() {
            if (!this.selectedRows || this.selectedRows.length === 0) return ''
            const totals = {}

            for (let i = 0; i < this.selectedRows.length; i++) {
                const row = this.selectedRows[i]
                const currency = (row.currency || 'rub').toLowerCase()
                const amount = row.amount !== null && row.amount !== undefined ? new BigNumber(row.amount) : new BigNumber(0)
                if (!totals[currency]) totals[currency] = new BigNumber(0)
                totals[currency] = totals[currency].plus(amount)
            }

            return Object.keys(totals)
                .map(currency => `${this.getFormatAmount(totals[currency].toString(), currency)} ${currency.toUpperCase()}`)
                .join(', ')
        }
    },
    methods: {
        getShort(text, length) {
            if (!text) return ''
            if (text.length <= length) return text
            return '...' + text.substring(text.length - length)
        },
        resetFilters() {
            this.lazyParams.filters = {
                id: { value: '', matchMode: 'contains' },
                ext_subscription_id: { value: '', matchMode: 'contains' },
                subscription_token: { value: '', matchMode: 'contains' },
                user_id: { value: '', matchMode: 'contains' },
                client_id: { value: '', matchMode: 'contains' },
                project_id: { value: '', matchMode: 'contains' },
                period_unit: { value: '', matchMode: 'contains' },
                status: { value: '', matchMode: 'contains' },
                bank_code: { value: '', matchMode: 'contains' },
                created_at: {
                    operator: FilterOperator.AND,
                    constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }]
                }
            }
            this.$emit('filter', this.lazyParams.filters)
        },
        getProjectLabel(project_id) {
            const project = this.projects.find(projectItem => projectItem.value === project_id)
            return project ? project.label : project_id
        },
        getBankLabel(bank_code) {
            const bank = this.banks.find(bankItem => bankItem.value === bank_code)
            return bank ? bank.label : bank_code
        },
        getUserById(user_id) {
            if (!this.users || this.users.length === 0) return null
            return this.users.find(user => user.id === user_id)
        },
        getUserTitle(user_id) {
            const user = this.getUserById(user_id)
            return user ? user.name : user_id
        },
        getFormatAmount(value, currency, digits = 2) {
            return this.helper.format_amount(value, currency, digits)
        },
        getStatusSeverity(status) {
            return this.helper.getStatusSeverity(status)
        },
        getStatusTitle(status) {
            return this.helper.getStatusTitle(status)
        },
        isUserInGroup(user, group) {
            return this.helper.isUserInGroup(user, group)
        },
        formatDate(dateStr) {
            return this.helper.formatDate(dateStr)
        },
        formatNullableDate(dateStr) {
            if (!dateStr) return '-'
            return this.formatDate(dateStr)
        },
        formatPeriod(periodCount, periodUnit) {
            if (!periodCount && !periodUnit) return '-'
            if (!periodUnit) return `${periodCount}`
            if (!periodCount) return periodUnit
            return `${periodCount} ${periodUnit}`
        },
        formatMetadata(metadata) {
            if (!metadata) return ''
            if (typeof metadata === 'string') return metadata
            try {
                return JSON.stringify(metadata)
            } catch (error) {
                return String(metadata)
            }
        },
        formatMetadataPretty(metadata) {
            if (!metadata) return ''
            if (typeof metadata === 'string') {
                try {
                    return JSON.stringify(JSON.parse(metadata), null, 2)
                } catch (error) {
                    return metadata
                }
            }
            try {
                return JSON.stringify(metadata, null, 2)
            } catch (error) {
                return String(metadata)
            }
        },
        openMetadataModal(metadata) {
            this.metadataDialog.content = this.formatMetadataPretty(metadata)
            this.metadataDialog.visible = true
        },
        copy(text) {
            if (text === null || text === undefined || text === '') return
            const value = typeof text === 'string' ? text : String(text)
            this.helper.copy_clipboard(value)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: value, life: 1000 })
        },
        async checkSubscriptionStatus(id) {
            this.loading_ = true
            this.loadingSubscriptionId = id
            try {
                const resp = await this.connect.GetAdminSubscriptionStatus(id).then(res => res)
                if (resp.result && resp.data) {
                    const status = resp.data.status ? resp.data.status : 'unknown'
                    const message = resp.data.message
                        ? `${resp.data.message}`
                        : `Статус: ${status}`
                    this.$toast.add({ severity: this.getStatusSeverity(status), summary: this.getStatusTitle(status), detail: message, life: 3000 })
                } else {
                    const message = resp && resp.message ? resp.message : 'Не удалось получить статус подписки'
                    this.$toast.add({ severity: 'warn', summary: 'Ошибка', detail: message, life: 3000 })
                }
            } catch (error) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Ошибка запроса', life: 1500 })
            } finally {
                this.loading_ = false
                this.loadingSubscriptionId = null
            }
        },
        openChargeDialog(id) {
            if (!id) return
            this.chargeDialog.id = id
            this.chargeDialog.visible = true
        },
        onSubscriptionCharged() {
            this.chargeDialog.visible = false
            this.chargeDialog.id = null
            this.loadSubscriptions()
        },
        openCancelDialog(id) {
            if (!id) return
            this.cancelDialog.id = id
            this.cancelDialog.visible = true
        },
        onSubscriptionCanceled() {
            this.cancelDialog.visible = false
            this.cancelDialog.id = null
            this.loadSubscriptions()
        },
        onPage(event) {
            this.lazyParams.first = event.first
            this.lazyParams.rows = event.rows
            this.lazyParams.page = event.page
            this.lazyParams.pageCount = event.pageCount
            this.$emit('lazyParams', this.lazyParams)
        },
        onSort(event) {
            if (event.sortField && event.sortOrder) {
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
            this.lazyParams.sort = this.sort
            this.$emit('lazyParams', this.lazyParams)
        },
        onFilter(params) {
            this.lazyParams.filters = params.filters
            this.$emit('filter', params.filters)
        },
        loadSubscriptions() {
            this.$emit('lazyParams', this.lazyParams)
        },
        exportCSV() {
            this.$refs.dt.exportCSV()
        },
        goToOrders(id) {
            if(!id) return
            window.open(`/admin/orders/?subscription_id=${id}`, '_blank')
        },
        goToEvents(id) {
            if(!id) return
            window.open(`/admin/subscriptions/events/?subscription_id=${id}`, '_blank')
        },
        goToAttempts(id) {
            if(!id) return
            window.open(`/admin/subscriptions/attempts/?subscription_id=${id}`, '_blank')
        }
    },
    async beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        this.dt = this.$refs.dt
    }
}
</script>